// Caps + containment on a gloss request's quote and passage (SPEC
// "Translation / gloss"), and the codes a refused request carries. The server
// runs them on the passage it cut from its own `originalText`; the popover
// runs the same function on its local copy to explain a refusal up front.
// Pure and dependency-free (testing-philosophy): exercised directly rather
// than only through the action.

import { ConvexError } from "convex/values";

/** Selections longer than this are not the short quotes/words this feature
 * is for, and would balloon the prompt size for no reader benefit. */
export const MAX_QUOTE_LENGTH = 500;

/** A containing paragraph beyond this is not "the sentence around the
 * selection" — same cost-amplification concern as the quote cap. */
export const MAX_PARAGRAPH_LENGTH = 5000;

export const GLOSS_INPUT_ERROR = {
  quoteTooLong: "quote_too_long",
  paragraphTooLong: "paragraph_too_long",
  notContained: "quote_not_contained",
  spanUnresolved: "span_unresolved",
} as const;
export type GlossInputErrorCode =
  (typeof GLOSS_INPUT_ERROR)[keyof typeof GLOSS_INPUT_ERROR];

/**
 * Throws a distinguishable `ConvexError({ code })` when `quote`/`paragraph`
 * exceed their caps, or `paragraph` does not contain `quote` verbatim.
 * Returns normally (no value) when the input is acceptable.
 */
export function assertValidGlossInput(quote: string, paragraph: string): void {
  if (quote.length > MAX_QUOTE_LENGTH) {
    throw new ConvexError({ code: GLOSS_INPUT_ERROR.quoteTooLong });
  }
  if (paragraph.length > MAX_PARAGRAPH_LENGTH) {
    throw new ConvexError({ code: GLOSS_INPUT_ERROR.paragraphTooLong });
  }
  if (!paragraph.includes(quote)) {
    throw new ConvexError({ code: GLOSS_INPUT_ERROR.notContained });
  }
}
